import { useIsChatObjectRefsEnabled } from '@/features/flags';
import type { SessionListItem } from '@/features/sessions/types';
import { WORK_CHAT_HOME_PATH, type LinkedContextItem } from '@heidi/agents/contracts';
import {
  artifactForLinkedItem,
  createPendingTokenIntent,
  currentWorkModeAtom,
  pendingComposerLinkedContextAtom,
  pendingWorkModeAtom,
  setComposerLinkedItemAtom,
  setOpenArtifactAtom,
} from '@heidi/agents/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useMobilePanel } from '../../mobile/use-mobile-panel';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { useOpenSessionFragment } from './use-open-session-fragment';

export interface RecentVisitActions {
  /** Attach the visit to the Work composer as linked context. */
  addToChat: () => void;
  /** Attach the visit's patient and open their record on the Work canvas. */
  openPatient: () => void;
  /** Open the session at its default landing page. */
  openSession: () => void;
}

/**
 * The three actions the Recents hover card offers for one visit.
 *
 * Two of them land on Work. `addToChat` always stages a token-preferring intent
 * for the Composer that owns the editor handle; `openPatient` keeps the existing
 * chip-and-canvas behaviour. Off-Work intents are staged before navigation so
 * Work home's entry reset cannot throw them away.
 *
 * Only `openPatient` opens a canvas. `addToChat` is deliberately the composer's
 * own `+` → Patients behaviour and nothing more: a token (or the chip fallback)
 * lands on the prompt and the page is otherwise left alone. Opening the session
 * canvas as well would make "add this to my next question" answer a question
 * the user did not ask.
 */
export function useRecentVisitActions(
  session: SessionListItem,
  /** The label the row renders, already resolved through the untitled fallback,
   *  so the composer chip cannot read blank where the sidebar read a name. */
  sessionLabel: string
): RecentVisitActions {
  const router = useRouter();
  const { dismissMobileSidebar } = useMobilePanel();
  const { openSession: openSessionAtDefaultTab } = useOpenSessionFragment();
  const stageLinkedContext = useSetAtom(pendingComposerLinkedContextAtom);
  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);
  const currentWorkMode = useAtomValue(currentWorkModeAtom);
  const chatObjectRefsEnabled = useIsChatObjectRefsEnabled();

  const linkOnWork = useCallback(
    (
      item: LinkedContextItem,
      {
        openArtifact,
        prefer = 'chip',
      }: { openArtifact: boolean; prefer?: 'token' | 'chip' }
    ) => {
      dismissMobileSidebar();

      const workPath = router.asPath.split('?')[0];
      const isWithinWork = workPath?.startsWith(WORK_CHAT_HOME_PATH) ?? false;
      const isWorkHome = workPath === WORK_CHAT_HOME_PATH;
      const canInsertTokenHere =
        prefer === 'token' &&
        chatObjectRefsEnabled &&
        isWorkHome &&
        currentWorkMode === 'work';

      if (isWithinWork) {
        if (canInsertTokenHere) {
          stageLinkedContext(createPendingTokenIntent(item, { openArtifact }));
          return;
        }
        setLinkedItem(item);
        if (!openArtifact) return;
        const artifact = artifactForLinkedItem(item);
        if (artifact) setOpenArtifact(artifact);
        return;
      }

      const canInsertTokenAtDestination = prefer === 'token' && chatObjectRefsEnabled;
      stageLinkedContext(
        canInsertTokenAtDestination
          ? createPendingTokenIntent(item, { openArtifact })
          : { item, openArtifact }
      );
      // Work mode only. Deliberately NOT `pendingWorkHomeViewAtom = 'new-chat'`,
      // which the sidebar's own New chat hand-off does set: Work home answers
      // that request by calling `resetComposerContext()`, in a layout effect
      // that runs AFTER the drain, so asking for a new chat and asking for a
      // chip are the same frame and the chip loses. The landing already shows
      // the composer, so the request bought nothing and cost the feature.
      //
      // The mode atom is safe — its effect sets the composer's Auto/Evidence
      // pill and touches no linked context — and it is what stops a clinician
      // whose saved home mode is Evidence landing on the wrong composer.
      setPendingWorkMode('work');
      void router.push(WORK_CHAT_HOME_PATH);
    },
    [
      dismissMobileSidebar,
      chatObjectRefsEnabled,
      currentWorkMode,
      router,
      setLinkedItem,
      setOpenArtifact,
      stageLinkedContext,
      setPendingWorkMode,
    ]
  );

  const addToChat = useCallback(() => {
    trackNavV3ButtonClicked({
      label: 'nav_v3_recent_visit_hover_action',
      click_source: 'add_to_chat',
      session_id: session.session_id,
    });
    linkOnWork(
      {
        type: 'session',
        sessionId: session.session_id,
        sessionName: sessionLabel,
        ...(session.patient_profile_name?.trim()
          ? { patientName: session.patient_profile_name.trim() }
          : session.patient?.name?.trim()
            ? { patientName: session.patient.name.trim() }
            : {}),
      },
      { openArtifact: false, prefer: 'token' }
    );
  }, [linkOnWork, session, sessionLabel]);

  const openPatient = useCallback(() => {
    const patientId = session.patient_profile_id;
    const patientName =
      session.patient_profile_name?.trim() || session.patient?.name?.trim();
    // Guarded by the card, which omits the action without both. Re-checked here
    // so a programmatic call cannot stage a patient chip that resolves to no
    // artifact and silently lands the user on an empty Work canvas.
    if (!patientId || !patientName) return;
    trackNavV3ButtonClicked({
      label: 'nav_v3_recent_visit_hover_action',
      click_source: 'patient',
      session_id: session.session_id,
    });
    linkOnWork({ type: 'patient', patientName, patientId }, { openArtifact: true });
  }, [linkOnWork, session]);

  const openSession = useCallback(() => {
    trackNavV3ButtonClicked({
      label: 'nav_v3_recent_visit_hover_action',
      click_source: 'open_session',
      session_id: session.session_id,
    });
    openSessionAtDefaultTab(session.session_id);
  }, [openSessionAtDefaultTab, session.session_id]);

  return { addToChat, openPatient, openSession };
}
